class CollectionQuickFilters extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-quick-filter-btn]'));
    this.subButtons = Array.from(this.querySelectorAll('[data-sub-filter-btn]'));
    if (!this.buttons.length) return;

    this.activeClass = 'is-active';
    // Per-value "param=value" facet pairs with zero matches in the current
    // category view, rebuilt from the rendered facet counts whenever the active
    // parent changes (see collectEmptyFilterValues). Lets a sub-filter pill
    // (e.g. Sale) auto-hide when its filter has no products in the category —
    // mirrors the homepage collection experience.
    this.emptyFilterValues = new Set();
    this.lastEmptiesParent = null;
    this.titleElement = this.querySelector('[data-quick-filter-title]');
    this.descriptionElement = this.querySelector('[data-quick-filter-description]');
    this.mirrorLinkElement = this.querySelector('[data-quick-filter-mirror-link]');
    this.showcaseTarget = this.querySelector('[data-quick-filter-showcase]');
    this.handleClick = this.onButtonClick.bind(this);
    this.handleSubClick = this.onSubButtonClick.bind(this);
    this.handlePopState = this.syncActiveState.bind(this);

    this.buttons.forEach((button) => button.addEventListener('click', this.handleClick));
    this.subButtons.forEach((button) => button.addEventListener('click', this.handleSubClick));
    window.addEventListener('popstate', this.handlePopState);
    this.observeGridChanges();
    this.syncActiveState();
  }

  disconnectedCallback() {
    if (this.buttons) {
      this.buttons.forEach((button) => button.removeEventListener('click', this.handleClick));
    }
    if (this.subButtons) {
      this.subButtons.forEach((button) => button.removeEventListener('click', this.handleSubClick));
    }
    window.removeEventListener('popstate', this.handlePopState);
    if (this.gridObserver) this.gridObserver.disconnect();
  }

  observeGridChanges() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;
    let timer;
    this.gridObserver = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => this.syncActiveState(), 150);
    });
    this.gridObserver.observe(grid, { childList: true, subtree: true });
  }

  onButtonClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (button.classList.contains(this.activeClass)) return;

    // Cache the outgoing grid (the initial server-rendered category included) before
    // switching, so returning to it later is an instant cache hit, not a fresh fetch.
    this.cacheCurrentGrid();

    const subToApply = this.getDefaultSubButton();
    const searchParams = this.buildSearchParams(button, subToApply);

    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(searchParams.toString());
      this.notifyUrlChanged();
    } else {
      const queryString = searchParams.toString();
      const target = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
      window.location.assign(target);
      return;
    }

    this.setActiveButton(button);
    this.setActiveSubButton(subToApply);
    this.updateSubFilterVisibility(button);
  }

  updateSubFilterVisibility(parentButton) {
    if (!this.subButtons.length) return;

    const hideList = (parentButton.dataset.hideSubfilters || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    this.subButtons.forEach((subBtn) => {
      const label = subBtn.textContent.trim().toLowerCase();
      const shouldHide = hideList.includes(label) || this.isSubFilterEmpty(subBtn);
      subBtn.style.display = shouldHide ? 'none' : '';
    });

    // If active sub-filter is now hidden, reset to default
    const activeSubBtn = this.subButtons.find((btn) => btn.classList.contains(this.activeClass));
    if (activeSubBtn && activeSubBtn.style.display === 'none') {
      const visibleDefault = this.subButtons.find(
        (btn) => btn.style.display !== 'none' && btn.dataset.default === 'true'
      );
      const firstVisible = this.subButtons.find((btn) => btn.style.display !== 'none');
      const newActive = visibleDefault || firstVisible;
      if (newActive) {
        newActive.click();
      }
    }
  }

  // Read per-value product counts from the live facet drawer. facets.liquid
  // renders a count-0 (and inactive) value with a `disabled` class on its
  // .facets__label plus a trailing "(0)" — both are checked. facets.js refreshes
  // these elements (renderFilters) before swapping the grid, so by the time the
  // grid MutationObserver fires the counts read here are current. Returns a Set
  // of "param=value" strings (e.g. "filter.p.tag=sale") that have no matches in
  // the current category view.
  collectEmptyFilterValues() {
    const empty = new Set();
    document.querySelectorAll('label.facets__label').forEach((label) => {
      const input = label.querySelector('input[name^="filter."]');
      if (!input || !input.name || !input.value) return;
      let isEmpty = label.classList.contains('disabled');
      if (!isEmpty) {
        const match = label.textContent.match(/\((\d+)\)/);
        if (match && parseInt(match[1], 10) === 0) isEmpty = true;
      }
      if (isEmpty) empty.add(`${input.name}=${input.value}`);
    });
    return empty;
  }

  // A sub-filter is "empty" when a `filter.` pair in its query has zero matches
  // in the current category view. Sort-only sub-filters (Best Sellers, New
  // Arrivals) carry no `filter.` param, so they are never treated as empty.
  isSubFilterEmpty(subBtn) {
    if (!this.emptyFilterValues || !this.emptyFilterValues.size) return false;
    const params = new URLSearchParams(subBtn.dataset.query || '');
    for (const [key, value] of params) {
      if (key.indexOf('filter.') === 0 && this.emptyFilterValues.has(`${key}=${value}`)) {
        return true;
      }
    }
    return false;
  }

  onSubButtonClick(event) {
    event.preventDefault();
    const subButton = event.currentTarget;
    if (subButton.classList.contains(this.activeClass)) return;

    this.cacheCurrentGrid();

    // Get active parent button
    const activeParentButton = this.buttons.find((btn) => btn.classList.contains(this.activeClass)) || this.buttons[0];
    const searchParams = this.buildSearchParams(activeParentButton, subButton);

    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.renderPage(searchParams.toString());
      this.notifyUrlChanged();
    } else {
      const queryString = searchParams.toString();
      const target = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
      window.location.assign(target);
    }

    this.setActiveSubButton(subButton);
  }

  notifyUrlChanged() {
    document.dispatchEvent(new CustomEvent('collection:url-changed', { detail: { url: window.location.href } }));
  }

  getDefaultSubButton() {
    if (!this.subButtons.length) return null;
    return this.subButtons.find((btn) => btn.dataset.default === 'true') || this.subButtons[0];
  }

  // Snapshot the currently displayed grid into FacetFiltersForm's cache under the
  // active parent+sub filter key, so returning to it — notably the initial,
  // server-rendered category, which is never otherwise cached — is instant. The
  // key is built via buildSearchParams (the same path a click uses), so it matches
  // the later lookup; the facets helper no-ops if that key is already cached.
  cacheCurrentGrid() {
    if (typeof FacetFiltersForm === 'undefined' || typeof FacetFiltersForm.cacheCurrentSection !== 'function') return;
    const activeParent = this.buttons.find((btn) => btn.classList.contains(this.activeClass));
    if (!activeParent) return;
    const activeSub = this.subButtons.find((btn) => btn.classList.contains(this.activeClass)) || this.getDefaultSubButton();
    FacetFiltersForm.cacheCurrentSection(this.buildSearchParams(activeParent, activeSub).toString());
  }

  buildSearchParams(button, subButton = null) {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    // Clear keys owned by ANY parent pill so swapping pills swaps the category
    // (including swapping to ALL, whose own data-query is empty). Drawer-applied
    // keys not owned by any pill are preserved.
    this.buttons.forEach((btn) => {
      this.parseParams(btn.dataset.query).forEach((_, key) => params.delete(key));
    });
    this.getClearParams(button).forEach((key) => params.delete(key));
    if (subButton) {
      this.subButtons.forEach((btn) => {
        this.parseParams(btn.dataset.query).forEach((_, key) => params.delete(key));
        if (btn.dataset.queryAllOverride) {
          this.parseParams(btn.dataset.queryAllOverride).forEach((_, key) => params.delete(key));
        }
      });
      this.getClearParams(subButton).forEach((key) => params.delete(key));
    }

    // Apply parent filter params
    const buttonParams = this.parseParams(button.dataset.query);
    buttonParams.forEach((value, key) => {
      if (value === '') {
        params.delete(key);
      } else {
        params.append(key, value);
      }
    });

    // Apply sub-filter params (use ALL override when the default parent is active)
    if (subButton) {
      const useAllOverride = button.dataset.default === 'true' && subButton.dataset.queryAllOverride;
      const subQuery = useAllOverride ? subButton.dataset.queryAllOverride : subButton.dataset.query;
      const subParams = this.parseParams(subQuery);
      subParams.forEach((value, key) => {
        if (value === '') {
          params.delete(key);
        } else {
          params.append(key, value);
        }
      });
    }

    return params;
  }

  parseParams(queryString) {
    return new URLSearchParams(queryString || '');
  }

  getClearParams(button) {
    const extra = button.dataset.clear || '';
    return extra
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
  }

  getControlledKeys() {
    if (this.controlledKeys) return this.controlledKeys;
    const keys = new Set(['page']);

    this.buttons.forEach((button) => {
      const params = this.parseParams(button.dataset.query);
      params.forEach((_, key) => keys.add(key));
      this.getClearParams(button).forEach((key) => keys.add(key));
    });

    this.subButtons.forEach((button) => {
      const params = this.parseParams(button.dataset.query);
      params.forEach((_, key) => keys.add(key));
      if (button.dataset.queryAllOverride) {
        const overrideParams = this.parseParams(button.dataset.queryAllOverride);
        overrideParams.forEach((_, key) => keys.add(key));
      }
      this.getClearParams(button).forEach((key) => keys.add(key));
    });

    this.controlledKeys = Array.from(keys);
    return this.controlledKeys;
  }

  matchesButton(button, currentParams, activeParent = null) {
    const queries = [button.dataset.query];
    if (activeParent && activeParent.dataset.default === 'true' && button.dataset.queryAllOverride) {
      queries.push(button.dataset.queryAllOverride);
    }

    for (const query of queries) {
      const buttonParams = this.parseParams(query);

      if (!buttonParams.toString()) {
        if (!this.getControlledKeys().some((key) => currentParams.has(key))) return true;
        continue;
      }

      let matches = true;
      buttonParams.forEach((value, key) => {
        const values = currentParams.getAll(key);
        if (!values.includes(value)) {
          matches = false;
        }
      });
      if (matches) return true;
    }

    return false;
  }

  syncActiveState() {
    if (!this.buttons || !this.buttons.length) return;
    const currentParams = new URLSearchParams(window.location.search);

    // When the URL contains params from a sub-filter's ALL override, the
    // default/ALL parent should stay active — not the parent whose category
    // happens to partially match the override's inclusion list.
    const defaultButton = this.buttons.find((btn) => btn.dataset.default === 'true');
    const allOverrideActive = defaultButton && this.subButtons.some((btn) => {
      if (!btn.dataset.queryAllOverride) return false;
      const overrideParams = this.parseParams(btn.dataset.queryAllOverride);
      let matches = true;
      overrideParams.forEach((value, key) => {
        if (!currentParams.getAll(key).includes(value)) matches = false;
      });
      return matches;
    });

    // Sync parent filter
    const activeButton = allOverrideActive
      ? defaultButton
      : (this.buttons.find((button) => this.matchesButton(button, currentParams)) ||
         defaultButton ||
         this.buttons[0]);

    if (activeButton) {
      this.setActiveButton(activeButton);
      // Recompute empty sub-filters only when the category (parent) changes: a
      // parent switch resets to its sort-only default sub, so the facet counts
      // reflect the category alone. Sub-filter clicks keep the same parent, so
      // reusing the cached counts avoids judging emptiness against a narrowed
      // sub-filtered view (which would wrongly hide sibling sub-filters).
      if (activeButton !== this.lastEmptiesParent) {
        this.emptyFilterValues = this.collectEmptyFilterValues();
        this.lastEmptiesParent = activeButton;
      }
      this.updateSubFilterVisibility(activeButton);
    }

    // Sync sub-filter
    if (this.subButtons.length) {
      const activeSubButton =
        this.subButtons.find((button) => this.matchesButton(button, currentParams, activeButton)) ||
        this.subButtons.find((button) => button.dataset.default === 'true') ||
        this.subButtons[0];

      if (activeSubButton) {
        this.setActiveSubButton(activeSubButton);

        // When the default/ALL parent is active and the sub-filter has an ALL
        // override, ensure the URL reflects the override params. This handles
        // direct navigation (e.g. ?sort_by=created-descending) where the
        // override categories aren't in the URL yet.
        if (
          activeButton &&
          activeButton.dataset.default === 'true' &&
          activeSubButton.dataset.queryAllOverride
        ) {
          const overrideParams = this.parseParams(activeSubButton.dataset.queryAllOverride);
          let needsRedirect = false;
          overrideParams.forEach((value, key) => {
            if (!currentParams.getAll(key).includes(value)) {
              needsRedirect = true;
            }
          });
          if (needsRedirect) {
            const correctedParams = this.buildSearchParams(activeButton, activeSubButton);
            if (typeof FacetFiltersForm !== 'undefined') {
              FacetFiltersForm.renderPage(correctedParams.toString());
              this.notifyUrlChanged();
            } else {
              const qs = correctedParams.toString();
              window.location.replace(`${window.location.pathname}${qs ? `?${qs}` : ''}`);
            }
          }
        }
      }
    }
  }

  setActiveButton(activeButton) {
    this.buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle(this.activeClass, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.updateMeta(activeButton);
    this.updateShowcase(activeButton);
  }

  setActiveSubButton(activeSubButton) {
    if (!activeSubButton) return;
    this.subButtons.forEach((button) => {
      const isActive = button === activeSubButton;
      button.classList.toggle(this.activeClass, isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  updateMeta(button) {
    if (!button) return;

    if (this.titleElement) {
      this.titleElement.textContent = button.dataset.heading || this.titleElement.dataset.defaultHeading || '';
    }

    if (this.descriptionElement) {
      const description = button.dataset.description || '';
      this.descriptionElement.textContent = description;
      this.descriptionElement.toggleAttribute('hidden', description === '');
    }

    if (this.mirrorLinkElement) {
      const label = button.dataset.mirrorLabel || this.mirrorLinkElement.dataset.defaultLabel || '';
      const baseUrl =
        button.dataset.mirrorUrlBase || this.mirrorLinkElement.dataset.defaultUrlBase || '';
      const targetUrl = this.buildMirrorUrl(baseUrl);

      this.mirrorLinkElement.textContent = label;

      if (targetUrl) {
        this.mirrorLinkElement.setAttribute('href', targetUrl);
        this.mirrorLinkElement.classList.remove('is-disabled');
        this.mirrorLinkElement.removeAttribute('aria-disabled');
        if (this.mirrorLinkElement.getAttribute('tabindex') === '-1') {
          this.mirrorLinkElement.removeAttribute('tabindex');
        }
      } else {
        this.mirrorLinkElement.removeAttribute('href');
        this.mirrorLinkElement.classList.add('is-disabled');
        this.mirrorLinkElement.setAttribute('aria-disabled', 'true');
        this.mirrorLinkElement.setAttribute('tabindex', '-1');
      }
    }
  }

  updateShowcase(button) {
    if (!this.showcaseTarget) return;
    const templateId = button?.dataset.showcaseTemplate;
    if (templateId) {
      const template = document.getElementById(templateId);
      if (template) {
        this.showcaseTarget.innerHTML = template.innerHTML;
        this.showcaseTarget.removeAttribute('hidden');
        return;
      }
    }
    this.showcaseTarget.innerHTML = '';
    this.showcaseTarget.setAttribute('hidden', '');
  }

  buildMirrorUrl(baseUrl) {
    if (!baseUrl) return '';
    const params = new URLSearchParams(window.location.search);
    params.delete('filter.p.m.custom.gender');
    const search = params.toString();
    if (!search) return baseUrl;
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${search}`;
  }
}

if (!customElements.get('collection-quick-filters')) {
  customElements.define('collection-quick-filters', CollectionQuickFilters);
}
